import { ChangeEvent, FC, ReactNode, useRef } from "react";
import { Flex, Spinner, Text } from "@chakra-ui/react";

import { CheckIcon } from "@shared/components/Icons";
import { UPLOAD_STATUS, UploadRow } from "@containers/Cases/hooks";
import { pluralize } from "@containers/Cases/utils/caseDocuments";

import CaseDocumentFileTag from "../CaseDocumentFileTag";
import { formatFileSize } from "../CaseDocuments.utils";
import CaseDocumentRemoveFileButton from "./CaseDocumentRemoveFileButton";
import CaseDocumentFailedFileRow from "./CaseDocumentFailedFileRow";

const STATUS_NOTES: Partial<Record<UPLOAD_STATUS, string>> = {
  [UPLOAD_STATUS.QUEUED]: "Waiting to upload",
  [UPLOAD_STATUS.UPLOADING]: "Uploading…",
};

interface FailedUploadRowProps {
  row: UploadRow;
  onReplace: (file: File) => void;
  onRemove: () => void;
}

const FailedUploadRow: FC<FailedUploadRowProps> = ({ row, onReplace, onRemove }) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Lets the same file be picked again, e.g. after it failed for a network error.
    event.target.value = "";
    if (file) onReplace(file);
  };

  return (
    <>
      <input ref={inputRef} type="file" hidden onChange={handleChange} />
      <CaseDocumentFailedFileRow
        file={{ name: row.file.name, mime_type: row.mimeType }}
        title="Upload failed"
        reason={row.error}
        onReplace={() => inputRef.current?.click()}
        onRemove={onRemove}
      />
    </>
  );
};

interface CaseDocumentUploadRowProps {
  row: UploadRow;
  onReplace: (file: File) => void;
  onRemove: () => void;
  /** Shown before the row's status icon */
  tag?: ReactNode;
}

const CaseDocumentUploadRow: FC<CaseDocumentUploadRowProps> = ({ row, onReplace, onRemove, tag }) => {
  const { file, mimeType, status, pages } = row;

  if (status === UPLOAD_STATUS.FAILED) {
    return <FailedUploadRow row={row} onReplace={onReplace} onRemove={onRemove} />;
  }

  const meta = [formatFileSize(file.size), pages !== null && pluralize(pages, "page")].filter(Boolean).join(" · ");

  return (
    <Flex align="center" gap={3} px="14px" py="11px" borderRadius="8px" border="1px solid" borderColor="gray.200">
      <CaseDocumentFileTag file={{ mime_type: mimeType }} />
      <Flex direction="column" gap="2px" flex={1} minW={0}>
        <Text truncate fontSize="14px" color="brand.modalHeaderText">
          {file.name}
        </Text>
        <Text fontSize="12px" color="brand.emptyStateText">
          {STATUS_NOTES[status] ?? meta}
        </Text>
      </Flex>

      {tag}
      {status === UPLOAD_STATUS.UPLOADING && <Spinner size="sm" color="brand.emptyStateText" />}
      {status === UPLOAD_STATUS.READY && <CheckIcon boxSize={4} color="brand.checkMark" />}
      <CaseDocumentRemoveFileButton
        fileName={file.name}
        disabled={status === UPLOAD_STATUS.UPLOADING}
        onClick={onRemove}
      />
    </Flex>
  );
};

export default CaseDocumentUploadRow;
